import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CompletedActivityPayloadType,
  SyncedExternalPlatforms
} from 'interfaces';
import {
  EMPTY_STRING,
  ACTIVITY_TYPES,
  FOCUS_SUMMARY_FORM_STATE
} from 'constants/general';
import {
  fetchUserLevelStats,
  getCompletedActivityDaySummary,
  getEveningActivityStats,
  getMorningActivityStats,
  getUserDetailsCurrentActivityProps,
  getWeeklyFocusBlockSummary,
  getMoreWeeklyFocusBlockSummary,
  getCompletedFocusBlocks,
  updateLongTermGoals,
  updateUserConsent,
  getWeeklyStats,
  getUserDetails,
  getLongTermGoals,
  getSyncedExternalPlatforms,
  getUserFocusModes,
  getUserSummary,
  getUserTimezone
} from './extra';
import { initialUserState } from 'store/initial-states';
import { initWeeklySummaryStats } from 'utils/helpers';
import {
  mergeFocusSummariesById,
  uniqueFocusSummariesById
} from 'utils/focus-summary.util';
import {
  UserDetailsTypes,
  WeeklyFocusBlockSummary
} from 'interfaces/user.interface';
import { Auth0User } from 'interfaces/auth.interface';
import { FocusMode } from 'interfaces/focus-mode.interface';

const user = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    updateWeeklySummaryLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isWeeklySummaryUpdating = payload;
    },
    updateMorningStatsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isMorningStatsUpdating = payload;
    },
    updateEveningStatsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isEveningStatsUpdating = payload;
    },
    updateUserDetails: (
      state,
      { payload }: PayloadAction<UserDetailsTypes>
    ) => {
      state.details = payload;
    },
    setFocusedModes: (
      state,
      {
        payload
      }: PayloadAction<{
        focusNames: FocusMode[];
      }>
    ) => {
      state.focus_modes = payload?.focusNames ?? [];
    },
    setIsUserAdmin: (state, action) => {
      state.isUserAdmin = action.payload;
    },
    updateIsCurrentActivityPropsUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isCurrentActivityPropsUpdating = payload;
    },
    updateLastCompletedActivityLog: (
      state,
      { payload }: PayloadAction<CompletedActivityPayloadType | null>
    ) => {
      state.last_completed_activity_log = payload;
    },
    updateIsMorningRoutineCompleted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isMorningRoutineCompleted = payload;
    },
    updateIsEveningRoutineCompleted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isEveningRoutineCompleted = payload;
    },
    updateIsBreakingRoutineCompleted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isBreakingRoutineCompleted = payload;
    },
    updateCompletedRoutines: (state, { payload }: PayloadAction<string[]>) => {
      state.completed_routines = payload;
      if (payload.includes(ACTIVITY_TYPES.MORNING)) {
        state.isMorningRoutineCompleted = true;
      }
      if (payload.includes(ACTIVITY_TYPES.EVENING)) {
        state.isBreakingRoutineCompleted = true;
        state.isEveningRoutineCompleted = true;
      }
    },
    toggleAreOnboardingStatsLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areOnboardingStatsLoading = payload;
    },
    toggleAreUserDetailsLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areUserDetailsLoading = payload;
    },
    updateMotivationalSummary: (state, { payload }: PayloadAction<string>) => {
      state.motivationalSummary += payload.replaceAll('"', '');
    },
    updateIsMotivationalSummaryLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isMotivationalSummaryLoading = payload;
      if (payload) {
        // reset motivational summary for each new generation
        state.motivationalSummary = EMPTY_STRING;
      }
    },
    updateIsMotivationalSummaryStopGenerating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isMotivationalSummaryStopGenerating = payload;
    },
    updateIsUpdatingUserPrivacyPolicy: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingUserPrivacyPolicy = payload;
    },
    updateIsDailySummaryFetching: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isDailySummaryFetching = payload;
    },
    updateIsLogTermGoalUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isLogTermGoalUpdating = payload;
    },
    updateIsFetchingLogTermGoal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingLogTermGoal = payload;
    },
    updateIsTrialUser: (state, { payload }: PayloadAction<boolean>) => {
      state.isTrialUser = payload;
    },
    updateIsWeeklyFocusBlockSummaryUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isWeeklyFocusBlockSummaryUpdating = payload;
    },
    updateWeeklyFocusBlockSummary: (
      state,
      { payload }: PayloadAction<WeeklyFocusBlockSummary[]>
    ) => {
      state.weeklyFocusBlockSummary = payload;
    },
    updateIsUsernameUpdating: (state, { payload }: PayloadAction<boolean>) => {
      state.isUsernameUpdating = payload;
    },
    updateUsername: (state, { payload }: PayloadAction<string>) => {
      state.details.username = payload;
    },
    updateIsUserLoggingOut: (state, { payload }: PayloadAction<boolean>) => {
      state.isUserLoggingOut = payload;
    },
    updateIsWeeklyStatsUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isWeeklyStatsUpdating = payload;
    },
    updateOpenAIPrompt: (state, { payload }: PayloadAction<string>) => {
      state.openAIPrompt = payload;
    },
    updateIsSavingUserFeedback: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSavingUserFeedback = payload;
    },
    updateShowThankYouModal: (state, { payload }: PayloadAction<boolean>) => {
      state.showThankYouModal = payload;
    },
    updateSyncedExternalPlatforms: (
      state,
      { payload }: PayloadAction<SyncedExternalPlatforms>
    ) => {
      state.synced_external_platforms = payload;
    },
    updateSyncedExternalPlatformStatus: (
      state,
      {
        payload: { platform, value }
      }: PayloadAction<{
        platform: keyof SyncedExternalPlatforms;
        value: boolean;
      }>
    ) => {
      state.synced_external_platforms[platform] = value;
    },
    updateShowTeamNameModal: (state, { payload }: PayloadAction<boolean>) => {
      state.showTeamNameModal = payload;
    },
    updateIsUpdatingUserProfileDescription: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingUserProfileDescription = payload;
    },
    updateIsUpdatingUserProfileImage: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingUserProfileImage = payload;
    },
    updateIsUpdatingUserTimezone: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingUserTimezone = payload;
    },
    setUserDetailsTimezone: (state, { payload }: PayloadAction<string>) => {
      if (state.details) {
        state.details.timezone = payload;
      }
    },
    updateUserMetadata: (
      state,
      {
        payload
      }: PayloadAction<{
        profile_image: {
          url: string;
          file_path?: string;
        };
        description: string;
      }>
    ) => {
      state.details.metadata = payload;
    },
    updateIsCancelingSubscription: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isCancelingSubscription = payload;
    },
    updateIsFetchingSyncedExternalPlatforms: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingSyncedExternalPlatforms = payload;
    },
    updateIsSendingEmailVerification: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSendingEmailVerification = payload;
    },
    updateAuth0User: (state, { payload }: PayloadAction<Auth0User>) => {
      state.auth0User = payload;
    },
    updateIsFetchingUserDetailsSummary: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingUserDetailsSummary = payload;
    },
    updateIsFetchingUserFocusModes: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingUserFocusModes = payload;
    },
    updateIsCompletedFocusBlocksFetching: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isCompletedFocusBlocksFetching = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMorningActivityStats.fulfilled, (state, { payload }) => {
        state.morningCompletionPercent =
          payload?.average_completion_percent ?? 0;
        state.isMorningStatsUpdating = false;
      })
      .addCase(getMorningActivityStats.rejected, (state) => {
        state.isMorningStatsUpdating = false;
      })
      .addCase(getEveningActivityStats.fulfilled, (state, { payload }) => {
        state.eveningCompletionPercent =
          payload?.average_completion_percent ?? 0;
        state.isEveningStatsUpdating = false;
      })
      .addCase(getEveningActivityStats.rejected, (state) => {
        state.isEveningStatsUpdating = false;
      })
      .addCase(
        getUserDetailsCurrentActivityProps.fulfilled,
        (state, { payload }) => {
          state.currentActivityProps = payload;
          state.isCurrentActivityPropsUpdating = false;
        }
      )
      .addCase(getUserDetailsCurrentActivityProps.rejected, (state) => {
        state.isCurrentActivityPropsUpdating = false;
      })
      .addCase(fetchUserLevelStats.fulfilled, (state, { payload }) => {
        state.onboardingStats = { ...payload };
        state.areOnboardingStatsLoading = false;
      })
      .addCase(fetchUserLevelStats.rejected, (state) => {
        state.areOnboardingStatsLoading = false;
      })
      .addCase(updateUserConsent.fulfilled, (state, { payload }) => {
        if (payload) {
          state.details.has_consented_to_terms_of_service = true;
        }
        state.isUpdatingUserPrivacyPolicy = false;
      })
      .addCase(updateLongTermGoals.fulfilled, (state, { payload }) => {
        state.details.long_term_goals = payload ?? [];
        state.isLogTermGoalUpdating = false;
      })
      .addCase(
        getCompletedActivityDaySummary.fulfilled,
        (state, { payload }) => {
          state.dailySummary.focusSummary = payload?.focusSummary ?? [];
          state.dailySummary.daySummaryAVG = payload?.daySummaryAVG ?? [];
          state.dailySummary.daySummarySUM = payload?.daySummarySUM ?? [];
          state.dailySummary.daySummaryDuration =
            payload?.daySummaryDuration ?? [];
          state.isDailySummaryFetching = false;
        }
      )
      .addCase(getWeeklyFocusBlockSummary.fulfilled, (state, { payload }) => {
        state.weeklyFocusBlockSummary = uniqueFocusSummariesById(payload ?? []);
        state.isWeeklyFocusBlockSummaryUpdating = false;
      })
      .addCase(
        getMoreWeeklyFocusBlockSummary.fulfilled,
        (state, { payload }) => {
          state.weeklyFocusBlockSummary = mergeFocusSummariesById(
            state.weeklyFocusBlockSummary ?? [],
            payload ?? []
          );
        }
      )
      .addCase(getWeeklyStats.fulfilled, (state, { payload }) => {
        state.weeklySummaryStats = payload?.length
          ? payload
          : initWeeklySummaryStats(FOCUS_SUMMARY_FORM_STATE.NUMBER_OF_DAYS);
        state.isWeeklyStatsUpdating = false;
      })
      .addCase(getUserDetails.fulfilled, (state, { payload }) => {
        if (payload) {
          state.details = payload;
        }
      })
      .addCase(getUserTimezone.pending, (state) => {
        state.isFetchingUserTimezone = true;
      })
      .addCase(getUserTimezone.fulfilled, (state) => {
        state.isFetchingUserTimezone = false;
      })
      .addCase(getUserTimezone.rejected, (state) => {
        state.isFetchingUserTimezone = false;
      })
      .addCase(getLongTermGoals.fulfilled, (state, { payload }) => {
        const goals = Array.isArray(payload) ? payload : [];
        state.details.long_term_goals = [
          ...(state.details?.long_term_goals ?? []).filter(
            (goal) => !goals.includes(goal)
          ),
          ...goals
        ];
        state.isFetchingLogTermGoal = false;
      })
      .addCase(getLongTermGoals.rejected, (state) => {
        state.isFetchingLogTermGoal = false;
      })
      .addCase(getSyncedExternalPlatforms.fulfilled, (state, { payload }) => {
        state.synced_external_platforms = payload;
        state.isFetchingSyncedExternalPlatforms = false;
      })
      .addCase(getSyncedExternalPlatforms.rejected, (state) => {
        state.isFetchingSyncedExternalPlatforms = false;
      })
      .addCase(getUserFocusModes.fulfilled, (state, { payload }) => {
        state.focus_modes = payload ?? [];
        state.isFetchingUserFocusModes = false;
      })
      .addCase(getUserFocusModes.rejected, (state) => {
        state.isFetchingUserFocusModes = false;
      })
      .addCase(getUserSummary.fulfilled, (state, { payload }) => {
        if (Object.keys(payload).length) {
          state.details = { ...state.details, ...payload };
        }
        state.isFetchingUserDetailsSummary = false;
      })
      .addCase(getUserSummary.rejected, (state) => {
        state.isFetchingUserDetailsSummary = false;
      })
      .addCase(getCompletedFocusBlocks.fulfilled, (state, { payload }) => {
        state.completedFocusBlocks = payload ?? [];
        state.isCompletedFocusBlocksFetching = false;
      })
      .addCase(getCompletedFocusBlocks.rejected, (state) => {
        state.isCompletedFocusBlocksFetching = false;
      });
  }
});

export const {
  updateUserDetails,
  setFocusedModes,
  updateMorningStatsLoading,
  updateEveningStatsLoading,
  updateWeeklySummaryLoading,
  setIsUserAdmin,
  updateIsCurrentActivityPropsUpdating,
  updateLastCompletedActivityLog,
  updateIsMorningRoutineCompleted,
  updateIsBreakingRoutineCompleted,
  updateIsEveningRoutineCompleted,
  updateCompletedRoutines,
  toggleAreOnboardingStatsLoading,
  toggleAreUserDetailsLoading,
  updateMotivationalSummary,
  updateIsMotivationalSummaryLoading,
  updateIsMotivationalSummaryStopGenerating,
  updateIsUpdatingUserPrivacyPolicy,
  updateIsDailySummaryFetching,
  updateIsLogTermGoalUpdating,
  updateIsTrialUser,
  updateIsWeeklyFocusBlockSummaryUpdating,
  updateWeeklyFocusBlockSummary,
  updateIsUsernameUpdating,
  updateUsername,
  updateIsUserLoggingOut,
  updateIsWeeklyStatsUpdating,
  updateOpenAIPrompt,
  updateIsSavingUserFeedback,
  updateShowThankYouModal,
  updateSyncedExternalPlatforms,
  updateShowTeamNameModal,
  updateIsUpdatingUserProfileImage,
  updateIsUpdatingUserProfileDescription,
  updateIsUpdatingUserTimezone,
  setUserDetailsTimezone,
  updateUserMetadata,
  updateIsCancelingSubscription,
  updateIsFetchingLogTermGoal,
  updateIsFetchingSyncedExternalPlatforms,
  updateIsSendingEmailVerification,
  updateSyncedExternalPlatformStatus,
  updateAuth0User,
  updateIsFetchingUserDetailsSummary,
  updateIsFetchingUserFocusModes,
  updateIsCompletedFocusBlocksFetching
} = user.actions;

export default user.reducer;
